import React, { useMemo, useState } from "react";
import ClearVerificationModal from "@/components/ClearVerificationModal";
import ClearStatusDisplay from "@/components/ClearStatusDisplay";
import { useVerification } from "@hooks/use_verification";
import { Body, Link } from "@sprout/typography";

import ClearLogoHorizontal from "@images/clear_logo_horizontal.png";
import { useQuery } from "@tanstack/react-query";
import useTranslation from "@hooks/use_translation";
import axios from "axios";
import { SecondaryButton } from "@sprout/buttons";

type VerificationStatus = {
  id?: number;
  verification_id?: string;
  status: string;
  token?: string;
  completed_at?: string | null;
  failed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  company_name?: string;
};

type VerificationWithTask = VerificationStatus & {
  identity_verification_task_id?: number | null;
  verification_url?: string;
};

const LearnMoreLink = () => {
  const { t } = useTranslation("clear_verification");

  return (
    <Link href="https://support.greenhouse.io/hc/en-us/articles/40497797083419" external secondary>
      {t("cta.learn_more")}
    </Link>
  );
};

const ClearProfileCard: React.FC = () => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const { t } = useTranslation("clear_verification");
  type VerificationShowResponse = {
    verification: VerificationWithTask | null;
    has_pending_identity_verification_task: boolean;
    pending_identity_verification_task_id?: number | null;
    pending_task_company_name?: string | null;
  };

  const { data, isLoading } = useQuery<VerificationShowResponse>({
    queryKey: ["clear-verification-status"],
    queryFn: async () => {
      const response = await axios.get("/clear_me/verification");
      return response.data as VerificationShowResponse;
    },
    staleTime: 0,
  });

  const pendingTaskId = data?.pending_identity_verification_task_id;
  const hasPendingTask = !!data?.has_pending_identity_verification_task;

  const { verifyWithClear } = useVerification({
    taskId: pendingTaskId || undefined,
    onSuccess: () => setShowVerifyModal(false),
  });

  const verificationStatus = useMemo<VerificationStatus | null>(() => {
    return (data?.verification as VerificationStatus) || null;
  }, [data]);

  return (
    <div>
      {!isLoading &&
        (hasPendingTask ? (
          <aside className="bg-white border rounded-lg p-6 w-full">
            <div className="flex flex-col gap-3">
              <div className="flex items-start">
                <img src={ClearLogoHorizontal} alt="CLEAR" className="h-6 w-auto" />
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-lg text-[14px] leading-5 bg-iris-e50">
                  {t("badge.action_required")}
                </span>
              </div>
              <Body className="text-base">{t("intro.blurb1")}</Body>
              <Body className="text-base">
                {t("intro.blurb2", { company_name: data?.pending_task_company_name })}
              </Body>
              <div className="pt-1 flex gap-3 items-center flex-wrap">
                <SecondaryButton size="medium" onClick={() => setShowVerifyModal(true)}>
                  {t("cta.verify_identity")}
                </SecondaryButton>
                <LearnMoreLink />
              </div>
            </div>
          </aside>
        ) : (
          verificationStatus && (
            <aside className="bg-white border rounded-lg p-6 w-full">
              <div className="flex flex-col gap-3">
                <div className="flex items-start">
                  <img src={ClearLogoHorizontal} alt="CLEAR" className="h-6 w-auto" />
                </div>

                <ClearStatusDisplay
                  status={verificationStatus.status}
                  companyName={verificationStatus.company_name}
                  verificationUrl={data?.verification?.verification_url}
                  completedAt={verificationStatus.completed_at}
                  failedAt={verificationStatus.failed_at}
                  updatedAt={verificationStatus.updated_at}
                >
                  <LearnMoreLink />
                </ClearStatusDisplay>
              </div>
            </aside>
          )
        ))}

      <ClearVerificationModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        companyName={data?.pending_task_company_name || ""}
        onVerify={verifyWithClear}
      />
    </div>
  );
};

export default ClearProfileCard;
